import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import talentLenzProductNavigationJumpArrowIcon from "../../images/talentlenz-product-navigation-jump-arrow-icon.png";
import talentLenzProductNavigationProductMark from "../../images/talentlenz-product-navigation-product-mark.png";

import "./TalentLenzProductNavigation.css";

const TALENTLENZ_PRODUCT_NAVIGATION_ITEMS = [
  {
    id: "problem",
    label: "Problem",
    href: "#talentlenz-application-volume-problem",
    targetSelector:
      "#talentlenz-application-volume-problem",
  },
  {
    id: "define",
    label: "Define",
    href: "#talentlenz-opportunity-definition",
    targetSelector: "#talentlenz-opportunity-definition",
  },
  {
    id: "assess",
    label: "Assess",
    href: "#talentlenz-assess",
    targetSelector: "#talentlenz-assess",
  },
  {
    id: "match",
    label: "Match",
    href: "#talentlenz-opportunity-match",
    targetSelector: "#talentlenz-opportunity-match",
  },
  {
    id: "shortlist",
    label: "Shortlist",
    href: "#talentlenz-match",
    targetSelector: "#talentlenz-match",
  },
  {
    id: "use-cases",
    label: "Use cases",
    href: "#talentlenz-opportunity-use-cases",
    targetSelector: "#talentlenz-opportunity-use-cases",
  },
];

const TALENTLENZ_PAGE_TOP_TARGET = {
  href: "#talentlenz-video-hero",
  targetSelector: "#talentlenz-video-hero",
};

const TALENTLENZ_DEFAULT_NAVIGATION_ID =
  TALENTLENZ_PRODUCT_NAVIGATION_ITEMS[0].id;

const TALENTLENZ_NAVIGATION_GAP = 12;
const TALENTLENZ_MANUAL_SELECTION_DURATION = 1400;

function talentLenzPrefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
  );
}

function isModifiedTalentLenzNavigationEvent(event) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

export default function TalentLenzProductNavigation() {
  const navigationRef = useRef(null);
  const navigationRailRef = useRef(null);
  const navigationJumpButtonRef = useRef(null);
  const navigationLinkRefs = useRef({});
  const manualNavigationRef = useRef(null);

  const [activeNavigationId, setActiveNavigationId] =
    useState(TALENTLENZ_DEFAULT_NAVIGATION_ID);

  const [railCanAdvance, setRailCanAdvance] =
    useState(false);

  /*
   * Detect the main AndUs Navbar when it is fixed or sticky and
   * position the TalentLenz navigation immediately beneath it.
   */
  useEffect(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return undefined;
    }

    const getExternalPinnedNavigationElements = () =>
      Array.from(
        document.querySelectorAll("header, nav")
      ).filter((element) => {
        if (
          element === navigationElement ||
          navigationElement.contains(element)
        ) {
          return false;
        }

        const styles = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        const isPinned =
          styles.position === "fixed" ||
          styles.position === "sticky";

        const isVisible =
          styles.display !== "none" &&
          styles.visibility !== "hidden" &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 8;

        return isPinned && isVisible;
      });

    const updateStickyPosition = () => {
      const pinnedNavigationBottom =
        getExternalPinnedNavigationElements().reduce(
          (currentBottom, element) => {
            const rect = element.getBoundingClientRect();

            return Math.max(currentBottom, rect.bottom);
          },
          0
        );

      navigationElement.style.setProperty(
        "--talentlenz-product-navigation-sticky-top",
        `${Math.max(
          0,
          Math.round(pinnedNavigationBottom)
        )}px`
      );
    };

    updateStickyPosition();

    window.addEventListener("resize", updateStickyPosition);

    let resizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(
        updateStickyPosition
      );

      getExternalPinnedNavigationElements().forEach(
        (element) => {
          resizeObserver.observe(element);
        }
      );
    }

    return () => {
      window.removeEventListener(
        "resize",
        updateStickyPosition
      );

      resizeObserver?.disconnect();
    };
  }, []);

  const getNavigationOffset = useCallback(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return 0;
    }

    const styles =
      window.getComputedStyle(navigationElement);

    const navigationTop = Number.parseFloat(styles.top);

    const topOffset = Number.isFinite(navigationTop)
      ? Math.max(0, navigationTop)
      : Math.max(
          0,
          navigationElement.getBoundingClientRect().top
        );

    return (
      topOffset +
      navigationElement.offsetHeight +
      TALENTLENZ_NAVIGATION_GAP
    );
  }, []);

  /*
   * Update the active link from the real positions of the six
   * navigation stages.
   *
   * Intermediary sections remain grouped under the stage that
   * precedes them:
   *
   * Assess includes the applicant evidence journey.
   * Shortlist includes responsible selection and access models.
   */
  useEffect(() => {
    let animationFrameId = null;

    const updateActiveNavigation = () => {
      animationFrameId = null;

      const navigationOffset = getNavigationOffset();

      const activationPosition =
        window.scrollY + navigationOffset + 4;

      const availableSections =
        TALENTLENZ_PRODUCT_NAVIGATION_ITEMS.map(
          (navigationItem) => {
            const sectionElement = document.querySelector(
              navigationItem.targetSelector
            );

            if (!sectionElement) {
              return null;
            }

            return {
              ...navigationItem,
              element: sectionElement,
              top:
                sectionElement.getBoundingClientRect().top +
                window.scrollY,
            };
          }
        ).filter(Boolean);

      if (availableSections.length === 0) {
        return;
      }

      const manualNavigation =
        manualNavigationRef.current;

      if (
        manualNavigation &&
        performance.now() < manualNavigation.expiresAt
      ) {
        const selectedSection = availableSections.find(
          (section) =>
            section.id === manualNavigation.id
        );

        if (selectedSection) {
          const selectedSectionViewportTop =
            selectedSection.element.getBoundingClientRect().top;

          const hasReachedSelectedSection =
            Math.abs(
              selectedSectionViewportTop - navigationOffset
            ) <= 24;

          if (!hasReachedSelectedSection) {
            setActiveNavigationId((currentId) =>
              currentId === manualNavigation.id
                ? currentId
                : manualNavigation.id
            );

            return;
          }
        }

        manualNavigationRef.current = null;
      } else if (manualNavigation) {
        manualNavigationRef.current = null;
      }

      let nextActiveNavigationId =
        availableSections[0].id;

      availableSections.forEach((section) => {
        if (section.top <= activationPosition) {
          nextActiveNavigationId = section.id;
        }
      });

      setActiveNavigationId((currentId) =>
        currentId === nextActiveNavigationId
          ? currentId
          : nextActiveNavigationId
      );
    };

    const requestNavigationUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        updateActiveNavigation
      );
    };

    updateActiveNavigation();

    window.addEventListener(
      "scroll",
      requestNavigationUpdate,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      requestNavigationUpdate
    );

    window.addEventListener(
      "hashchange",
      requestNavigationUpdate
    );

    return () => {
      window.removeEventListener(
        "scroll",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "resize",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "hashchange",
        requestNavigationUpdate
      );

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [getNavigationOffset]);

  const updateRailCanAdvance = useCallback(() => {
    const navigationRail = navigationRailRef.current;

    if (!navigationRail) {
      return;
    }

    const maximumScrollLeft = Math.max(
      0,
      navigationRail.scrollWidth -
        navigationRail.clientWidth
    );

    const canAdvance =
      maximumScrollLeft > 2 &&
      navigationRail.scrollLeft <
        maximumScrollLeft - 2;

    setRailCanAdvance((currentValue) =>
      currentValue === canAdvance
        ? currentValue
        : canAdvance
    );
  }, []);

  useEffect(() => {
    const navigationRail = navigationRailRef.current;

    if (!navigationRail) {
      return undefined;
    }

    let animationFrameId = null;
    let effectIsActive = true;

    const requestRailUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        () => {
          animationFrameId = null;
          updateRailCanAdvance();
        }
      );
    };

    navigationRail.addEventListener(
      "scroll",
      requestRailUpdate,
      { passive: true }
    );

    window.addEventListener("resize", requestRailUpdate);

    const railResizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(requestRailUpdate)
        : null;

    railResizeObserver?.observe(navigationRail);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (effectIsActive) {
          requestRailUpdate();
        }
      });
    }

    requestRailUpdate();

    return () => {
      effectIsActive = false;

      navigationRail.removeEventListener(
        "scroll",
        requestRailUpdate
      );

      window.removeEventListener(
        "resize",
        requestRailUpdate
      );

      railResizeObserver?.disconnect();

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [updateRailCanAdvance]);

  /*
   * Keep the active navigation link visible in the horizontal rail.
   */
  useEffect(() => {
    const navigationRail = navigationRailRef.current;

    const activeLink =
      navigationLinkRefs.current[activeNavigationId];

    if (
      !navigationRail ||
      !activeLink ||
      navigationRail.scrollWidth <=
        navigationRail.clientWidth
    ) {
      return;
    }

    const jumpButtonWidth =
      navigationJumpButtonRef.current?.offsetWidth || 0;

    const visibleStart =
      navigationRail.scrollLeft + 10;

    const visibleEnd =
      navigationRail.scrollLeft +
      navigationRail.clientWidth -
      jumpButtonWidth -
      10;

    const linkStart = activeLink.offsetLeft;
    const linkEnd =
      activeLink.offsetLeft + activeLink.offsetWidth;

    let nextScrollLeft = navigationRail.scrollLeft;

    if (linkStart < visibleStart) {
      nextScrollLeft = linkStart - 18;
    } else if (linkEnd > visibleEnd) {
      nextScrollLeft =
        linkEnd -
        navigationRail.clientWidth +
        jumpButtonWidth +
        18;
    }

    const maximumScrollLeft = Math.max(
      0,
      navigationRail.scrollWidth -
        navigationRail.clientWidth
    );

    nextScrollLeft = Math.min(
      maximumScrollLeft,
      Math.max(0, nextScrollLeft)
    );

    if (
      Math.abs(
        nextScrollLeft - navigationRail.scrollLeft
      ) > 1
    ) {
      navigationRail.scrollTo({
        left: nextScrollLeft,
        behavior: talentLenzPrefersReducedMotion()
          ? "auto"
          : "smooth",
      });
    }
  }, [activeNavigationId]);

  const handleNavigationTarget = (
    event,
    destination,
    nextActiveNavigationId
  ) => {
    if (isModifiedTalentLenzNavigationEvent(event)) {
      return;
    }

    const targetElement = document.querySelector(
      destination.targetSelector
    );

    if (!targetElement) {
      return;
    }

    event.preventDefault();

    if (nextActiveNavigationId) {
      manualNavigationRef.current = {
        id: nextActiveNavigationId,
        expiresAt:
          performance.now() +
          TALENTLENZ_MANUAL_SELECTION_DURATION,
      };

      setActiveNavigationId(nextActiveNavigationId);
    } else {
      manualNavigationRef.current = null;
    }

    const navigationOffset = getNavigationOffset();

    const targetScrollPosition =
      targetElement.getBoundingClientRect().top +
      window.scrollY -
      navigationOffset;

    if (window.location.hash !== destination.href) {
      window.history.replaceState(
        null,
        "",
        destination.href
      );
    }

    window.scrollTo({
      top: Math.max(
        0,
        Math.round(targetScrollPosition)
      ),
      behavior: talentLenzPrefersReducedMotion()
        ? "auto"
        : "smooth",
    });
  };

  const handleRailJump = () => {
    const navigationRail = navigationRailRef.current;

    if (!navigationRail) {
      return;
    }

    const jumpButtonWidth =
      navigationJumpButtonRef.current?.offsetWidth || 0;

    const visibleRightEdge =
      navigationRail.scrollLeft +
      navigationRail.clientWidth -
      jumpButtonWidth;

    const nextHiddenLink =
      TALENTLENZ_PRODUCT_NAVIGATION_ITEMS.map(
        (navigationItem) =>
          navigationLinkRefs.current[navigationItem.id]
      )
        .filter(Boolean)
        .find(
          (linkElement) =>
            linkElement.offsetLeft +
              linkElement.offsetWidth >
            visibleRightEdge + 2
        );

    const maximumScrollLeft = Math.max(
      0,
      navigationRail.scrollWidth -
        navigationRail.clientWidth
    );

    const requestedScrollLeft = nextHiddenLink
      ? nextHiddenLink.offsetLeft - 18
      : navigationRail.scrollLeft +
        navigationRail.clientWidth * 0.72;

    navigationRail.scrollTo({
      left: Math.min(
        maximumScrollLeft,
        Math.max(0, requestedScrollLeft)
      ),
      behavior: talentLenzPrefersReducedMotion()
        ? "auto"
        : "smooth",
    });
  };

  return (
    <nav
      ref={navigationRef}
      id="talentlenz-product-navigation"
      className="talentlenz-product-navigation"
      aria-label="TalentLenz page navigation"
    >
      <div className="talentlenz-product-navigation__inner">
        <a
          className="talentlenz-product-navigation__brand"
          href={TALENTLENZ_PAGE_TOP_TARGET.href}
          aria-label="Return to the top of the TalentLenz page"
          onClick={(event) =>
            handleNavigationTarget(
              event,
              TALENTLENZ_PAGE_TOP_TARGET,
              TALENTLENZ_DEFAULT_NAVIGATION_ID
            )
          }
        >
          <img
            className="talentlenz-product-navigation__brand-mark"
            src={talentLenzProductNavigationProductMark}
            alt=""
            aria-hidden="true"
            draggable={false}
          />

          <span className="talentlenz-product-navigation__brand-name">
            TalentLenz
          </span>
        </a>

        <div className="talentlenz-product-navigation__rail-shell">
          <div
            ref={navigationRailRef}
            className="talentlenz-product-navigation__rail"
          >
            {TALENTLENZ_PRODUCT_NAVIGATION_ITEMS.map(
              (navigationItem) => {
                const isActive =
                  activeNavigationId === navigationItem.id;

                return (
                  <a
                    key={navigationItem.id}
                    ref={(linkElement) => {
                      if (linkElement) {
                        navigationLinkRefs.current[
                          navigationItem.id
                        ] = linkElement;
                      } else {
                        delete navigationLinkRefs.current[
                          navigationItem.id
                        ];
                      }
                    }}
                    className={[
                      "talentlenz-product-navigation__link",
                      isActive
                        ? "talentlenz-product-navigation__link--active"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    href={navigationItem.href}
                    aria-current={
                      isActive ? "location" : undefined
                    }
                    onClick={(event) =>
                      handleNavigationTarget(
                        event,
                        navigationItem,
                        navigationItem.id
                      )
                    }
                  >
                    <span className="talentlenz-product-navigation__link-label">
                      {navigationItem.label}
                    </span>
                  </a>
                );
              }
            )}
          </div>

          <button
            ref={navigationJumpButtonRef}
            type="button"
            className={[
              "talentlenz-product-navigation__jump-button",
              !railCanAdvance
                ? "talentlenz-product-navigation__jump-button--hidden"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={handleRailJump}
            aria-label="Show the remaining TalentLenz navigation items"
            aria-hidden={!railCanAdvance}
            tabIndex={railCanAdvance ? 0 : -1}
          >
            <img
              className="talentlenz-product-navigation__jump-icon"
              src={
                talentLenzProductNavigationJumpArrowIcon
              }
              alt=""
              aria-hidden="true"
              draggable={false}
            />
          </button>
        </div>
      </div>
    </nav>
  );
}